import { useOrganization, useUser } from '@clerk/react';
import { useCallback, useEffect, useMemo } from 'react';
import type { ShareableUser } from '@/types';

export interface SequenceOwner {
  name: string;
  /** Their profile photo; null when they have none (not Clerk's default). */
  imageUrl: string | null;
  /** False while the membership list is still loading or the user has left the org. */
  resolved: boolean;
}

/**
 * The organization's memberships with every page pulled, so a team larger than
 * one page still resolves each owner instead of showing "Former member".
 */
function usePagedMemberships() {
  const { memberships, isLoaded } = useOrganization({
    memberships: { pageSize: 100, infinite: true, keepPreviousData: true },
  });
  useEffect(() => {
    if (memberships?.hasNextPage && !memberships.isFetching) {
      memberships.fetchNext();
    }
  }, [memberships]);
  return { memberships, isLoaded };
}

/** Organization members in the shape the list filter bar expects. */
export function useOrgMembers(): {
  members: ShareableUser[];
  isLoaded: boolean;
} {
  const { user } = useUser();
  const { memberships, isLoaded } = usePagedMemberships();

  const members = useMemo(() => {
    const list: ShareableUser[] = (memberships?.data ?? []).flatMap(
      (member) => {
        const data = member.publicUserData;
        if (!data?.userId) return [];
        return [
          {
            user_id: data.userId,
            first_name: data.firstName ?? undefined,
            last_name: data.lastName ?? undefined,
            email: data.identifier,
            image_url: data.hasImage ? data.imageUrl : undefined,
          },
        ];
      }
    );
    if (user && !list.some((member) => member.user_id === user.id)) {
      list.unshift({
        user_id: user.id,
        first_name: user.firstName ?? undefined,
        last_name: user.lastName ?? undefined,
        email: user.primaryEmailAddress?.emailAddress,
        image_url: user.hasImage ? user.imageUrl : undefined,
      });
    }
    return list;
  }, [memberships?.data, user]);

  return {
    members,
    isLoaded: isLoaded && Boolean(memberships) && !memberships?.hasNextPage,
  };
}

/**
 * Resolves a sequence's `owner_user_id` (a Clerk user id) to a display name and
 * avatar via the organization's membership list, so the UI never shows a raw
 * `user_…` id. Call once per screen and pass the resolver down; every lookup
 * is an in-memory scan of the already-loaded members.
 */
export function useSequenceOwners(): (userId: string) => SequenceOwner {
  const { user } = useUser();
  const { memberships, isLoaded } = usePagedMemberships();

  return useCallback(
    (userId: string): SequenceOwner => {
      if (user && userId === user.id) {
        return {
          name:
            user.fullName || user.primaryEmailAddress?.emailAddress || 'You',
          imageUrl: user.hasImage ? user.imageUrl : null,
          resolved: true,
        };
      }
      const data = memberships?.data?.find(
        (member) => member.publicUserData?.userId === userId
      )?.publicUserData;
      if (data) {
        const name =
          [data.firstName, data.lastName].filter(Boolean).join(' ') ||
          data.identifier;
        return {
          name,
          imageUrl: data.hasImage ? data.imageUrl : null,
          resolved: true,
        };
      }
      const loading =
        !isLoaded ||
        !memberships ||
        memberships.isLoading ||
        memberships.hasNextPage;
      return {
        name: loading ? 'Loading…' : 'Former member',
        imageUrl: null,
        resolved: false,
      };
    },
    [user, memberships, isLoaded]
  );
}

export function ownerInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || '?'
  );
}
